/** Изменения команды — SPEC-2 §5. */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity as ActivityIcon, Bot, BookOpen, Eye, User as UserIcon } from "lucide-react";
import { Link } from "react-router-dom";

import { Activity, Me } from "@/api/resources";
import type { ActivityOut } from "@/api/types";
import { FilterChip } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { GroupLabel, ScreenTitle, Section } from "@/components/ui/Section";
import { SkeletonList } from "@/components/ui/Skeleton";
import { activityLabel } from "@/lib/activity";
import { formatFullDate, parseDate } from "@/lib/format";
import { nameOf, usePeople } from "@/lib/people";
import { useBackButton } from "@/lib/telegram-ui";

type Filter = "all" | "partner" | "agent" | "knowledge";

const FILTERS: { value: Filter; label: string }[] = [
  { value: "all", label: "все" },
  { value: "partner", label: "партнёр" },
  { value: "agent", label: "агент" },
  { value: "knowledge", label: "знания" },
];

/** Entity types that count as "knowledge" rather than day-to-day work. */
const KNOWLEDGE_ENTITIES = new Set(["method"]);

export function ChangesView() {
  // Spec 020 D5: no longer a tab — opened from «Сейчас».
  useBackButton("/");
  const [filter, setFilter] = useState<Filter>("all");
  const me = useQuery({ queryKey: ["me"], queryFn: Me.get });
  const people = usePeople();
  const feed = useQuery({
    queryKey: ["activity", "feed"],
    queryFn: () => Activity.list({ limit: 100 }),
  });

  const visible = useMemo(() => {
    const rows = feed.data ?? [];
    switch (filter) {
      case "partner":
        return rows.filter((r) => me.data && r.actor_id !== me.data.id);
      case "agent":
        // `via_token_id` is the only thing that separates "Костя сделал" from
        // "Костя через cc-skill сделал" — `actor_id` is the token's owner in
        // both cases.
        return rows.filter((r) => r.via_token_id != null);
      case "knowledge":
        return rows.filter((r) => KNOWLEDGE_ENTITIES.has(r.entity_type));
      case "all":
      default:
        return rows;
    }
  }, [feed.data, filter, me.data]);

  const days = useMemo(() => groupByDay(visible), [visible]);

  return (
    <div className="space-y-4 pt-2">
      <ScreenTitle eyebrow="Что произошло, пока тебя не было"
        trailing={<Link to="/settings/notifications" className="text-meta text-accent">Уведомления</Link>}>Изменения</ScreenTitle>

      {/* Spec 013 §8 — the feed lists events; the overview says what they mean. */}
      <Link to="/overview" className="press-surface flex min-h-touch items-center gap-2 rounded-card bg-sbg p-3 text-cell text-text">
        <Eye className="h-[18px] w-[18px] shrink-0 text-accent" aria-hidden="true" />
        <span className="min-w-0 flex-1">Коротко: что ждёт тебя и кто чем занят — «Что происходит»</span>
      </Link>

      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
        {FILTERS.map(({ value, label }) => (
          <FilterChip key={value} active={filter === value} onClick={() => setFilter(value)}>
            {label}
          </FilterChip>
        ))}
      </div>

      {feed.isLoading && <SkeletonList count={4} />}

      {feed.isError && (
        <Card className="space-y-2">
          <p className="text-cell text-text">Лента не загрузилась.</p>
          <Button size="compact" variant="secondary" onClick={() => void feed.refetch()}>
            Повторить
          </Button>
        </Card>
      )}

      {!feed.isLoading && !feed.isError && visible.length === 0 && (
        <EmptyState
          icon={<ActivityIcon className="h-8 w-8" aria-hidden="true" />}
          title="Тихо. Пока никто ничего не менял."
          description={filter === "all" ? undefined : "Попробуй другой фильтр."}
        />
      )}

      {days.map(({ label, rows }) => (
        <Section key={label}>
          <GroupLabel>{label}</GroupLabel>
          {rows.map((row) => (
            <ChangeRow key={row.id} row={row} actorName={nameOf(people.data, row.actor_id)} />
          ))}
        </Section>
      ))}
    </div>
  );
}

function ChangeRow({ row, actorName }: { row: ActivityOut; actorName: string | null }) {
  const described = activityLabel(row);
  const isAgent = row.via_token_id != null;
  const isKnowledge = KNOWLEDGE_ENTITIES.has(row.entity_type);
  const target = linkFor(row);

  const body = (
    <div className="flex items-start gap-2">
      {isKnowledge ? (
        <BookOpen className="mt-0.5 h-[18px] w-[18px] shrink-0 text-hint" aria-hidden="true" />
      ) : isAgent ? (
        <Bot className="mt-0.5 h-[18px] w-[18px] shrink-0 text-hint" aria-hidden="true" />
      ) : (
        <UserIcon className="mt-0.5 h-[18px] w-[18px] shrink-0 text-hint" aria-hidden="true" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-cell text-text">
          {actorName ?? "Кто-то"}
          {/* The whole reason via_token_id reaches the client. */}
          {isAgent && <span className="text-hint"> через cc-skill</span>} {described}
        </p>
        {reasonOf(row) && <p className="text-meta text-hint">{reasonOf(row)}</p>}
      </div>
    </div>
  );

  if (!target) return <Card>{body}</Card>;
  return (
    <Link to={target} className="press-surface block rounded-card bg-sbg p-3">
      {body}
    </Link>
  );
}

/** A `method.review_suggested` carries the reason; showing it is the point. */
function reasonOf(row: ActivityOut): string | null {
  if (row.type !== "method.review_suggested") return null;
  const reason = row.payload?.reason;
  return typeof reason === "string" ? `Причина: ${reason}` : "Предложен пересмотр";
}

function linkFor(row: ActivityOut): string | null {
  if (row.entity_type === "method") return `/knowledge/${row.entity_id}`;
  if (row.entity_type === "task") return `/tasks/${row.entity_id}`;
  return null;
}

function groupByDay(rows: ActivityOut[]): { label: string; rows: ActivityOut[] }[] {
  const groups = new Map<string, ActivityOut[]>();
  for (const row of rows) {
    const at = parseDate(row.created_at);
    const key = at ? at.toDateString() : "—";
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }
  return [...groups.values()].map((items) => {
    const at = parseDate(items[0].created_at);
    return { label: at ? formatFullDate(at) : "Когда-то", rows: items };
  });
}
